import {
  Button,
  Flex,
  FormControl,
  Icon,
  Input,
  Select,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdRefresh } from 'react-icons/md';
import Card from 'components/card/Card';
import { useFinance } from 'contexts/FinanceContext';
import { monthLabel } from 'lib/finance';
import { TransactionType } from 'types/finance';

export default function FinanceFilters() {
  const {
    filters,
    categories,
    setFilters,
    resetFilters,
    availableMonthKeys,
  } = useFinance();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const iconColor = useColorModeValue('brand.500', 'white');
  const bgHover = useColorModeValue(
    { bg: 'secondaryGray.400' },
    { bg: 'whiteAlpha.50' }
  );
  const bgFocus = useColorModeValue(
    { bg: 'secondaryGray.300' },
    { bg: 'whiteAlpha.100' }
  );

  const monthSelected = filters.month && filters.month !== 'all';

  return (
    <Card alignItems="center" flexDirection="column" w="100%" mb="20px">
      <Flex
        align={{ base: 'stretch', lg: 'center' }}
        justify="space-between"
        w="100%"
        px="15px"
        py="10px"
        direction={{ base: 'column', lg: 'row' }}
        gap="12px"
      >
        <Text
          me={{ lg: 'auto' }}
          color={textColor}
          fontSize="xl"
          fontWeight="700"
          lineHeight="100%"
        >
          Filters
        </Text>

        <Flex
          wrap="wrap"
          gap="10px"
          align="center"
          flex="1"
          justify={{ base: 'flex-start', lg: 'flex-end' }}
        >
          <Select
            fontSize="sm"
            fontWeight="700"
            variant="subtle"
            w={{ base: '100%', sm: '180px' }}
            h="40px"
            borderRadius="16px"
            value={filters.month || 'all'}
            onChange={(e) => setFilters({ month: e.target.value })}
          >
            <option value="all">All months</option>
            {availableMonthKeys.map((key) => (
              <option key={key} value={key}>
                {monthLabel(key)}
              </option>
            ))}
          </Select>

          {!monthSelected && (
            <>
              <FormControl w={{ base: '100%', sm: '148px' }}>
                <Input
                  type="date"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColorSecondary}
                  bg={boxBg}
                  border="0px solid"
                  borderRadius="16px"
                  h="40px"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ dateFrom: e.target.value })}
                />
              </FormControl>

              <FormControl w={{ base: '100%', sm: '148px' }}>
                <Input
                  type="date"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColorSecondary}
                  bg={boxBg}
                  border="0px solid"
                  borderRadius="16px"
                  h="40px"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ dateTo: e.target.value })}
                />
              </FormControl>
            </>
          )}

          <Select
            fontSize="sm"
            fontWeight="700"
            variant="subtle"
            w={{ base: '100%', sm: '130px' }}
            h="40px"
            borderRadius="16px"
            value={filters.type}
            onChange={(e) =>
              setFilters({ type: e.target.value as TransactionType | 'all' })
            }
          >
            <option value="all">All types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </Select>

          <Select
            fontSize="sm"
            fontWeight="700"
            variant="subtle"
            w={{ base: '100%', sm: '170px' }}
            h="40px"
            borderRadius="16px"
            value={filters.categoryId}
            onChange={(e) => setFilters({ categoryId: e.target.value })}
          >
            <option value="">All categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </Select>

          <Button
            alignItems="center"
            justifyContent="center"
            bg={boxBg}
            _hover={bgHover}
            _focus={bgFocus}
            _active={bgFocus}
            w="40px"
            h="40px"
            minW="40px"
            p="0"
            lineHeight="100%"
            borderRadius="10px"
            onClick={resetFilters}
            aria-label="Reset filters"
          >
            <Icon as={MdRefresh} color={iconColor} w="20px" h="20px" />
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}
